import { useState, useRef } from "react"
import { FaFileUpload, FaTimes } from "react-icons/fa"
import { Button } from "@/modules/shared/ui/components/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/modules/shared/ui/components/dialog"
import EmployeeForm, {
  EmployeeFormValues,
  DocumentToBeSigned,
} from "@/modules/onboarding/ui/components/EmployeeForm"
import {
  FileCategory,
  useFileValidation,
} from "@/modules/shared/hooks/use-file-validation"

export function AddEditEmployeeDialog({
  open,
  onOpenChange,
  formId,
  submitLabel,
  isSubmitting,
  availableJobs,
  onSubmit,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  formId: string
  submitLabel: string
  isSubmitting: boolean
  availableJobs?: Array<{ id: string; title: string }>
  onSubmit: (formData: FormData) => void
}) {
  const [documentsToBeSigned, setDocumentsToBeSigned] = useState<
    DocumentToBeSigned[]
  >([])
  const [isDragging, setIsDragging] = useState(false)

  const { fileError, validateAndSetFile } = useFileValidation({
    category: FileCategory.Document,
  })

  const fileInputRef = useRef<HTMLInputElement>(null)

  const BYTES_PER_KB = 1024

  const formatFileSize = (bytes: number): string => {
    if (bytes < BYTES_PER_KB) return `${bytes} B`
    if (bytes < BYTES_PER_KB * BYTES_PER_KB)
      return `${(bytes / BYTES_PER_KB).toFixed(1)} KB`
    return `${(bytes / (BYTES_PER_KB * BYTES_PER_KB)).toFixed(1)} MB`
  }

  const handleDragOver = (
    e: React.DragEvent,
    setDragging: (dragging: boolean) => void,
  ) => {
    e.preventDefault()
    setDragging(true)
  }

  const handleDragLeave = (
    e: React.DragEvent,
    setDragging: (dragging: boolean) => void,
  ) => {
    e.preventDefault()
    setDragging(false)
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)
    const files = Array.from(e.dataTransfer.files)
    handleMultipleFiles(files)
  }

  const handleMultipleFiles = (files: File[]) => {
    const validFiles: DocumentToBeSigned[] = []
    files.forEach((file) => {
      validateAndSetFile(file, (validFile) => {
        validFiles.push({ file: validFile, name: validFile.name })
      })
    })
    if (validFiles.length > 0) {
      setDocumentsToBeSigned((prev) => [...prev, ...validFiles])
    }
  }

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (files) {
      handleMultipleFiles(Array.from(files))
      e.target.value = ""
    }
  }

  const handleRemoveDocument = (index: number) => {
    setDocumentsToBeSigned((prev) => prev.filter((_, i) => i !== index))
  }

  const handleSubmit = (employeeData: EmployeeFormValues) => {
    const formData = new FormData()

    formData.append("FirstName", employeeData.firstName)
    formData.append("LastName", employeeData.lastName)
    formData.append("Email", employeeData.email)
    formData.append("Position", employeeData.jobTitle)
    if (employeeData.jobStartDate) {
      formData.append("StartDate", employeeData.jobStartDate.toISOString())
    }

    if (
      employeeData.documentsNeeded &&
      employeeData.documentsNeeded.length > 0
    ) {
      employeeData.documentsNeeded.forEach((doc) => {
        formData.append("DocumentsNeeded", doc)
      })
    }

    if (documentsToBeSigned.length > 0) {
      documentsToBeSigned.forEach((doc) => {
        formData.append("DocumentsToBeSigned", doc.file)
      })
    }

    if (
      employeeData.workflowTemplateIds &&
      employeeData.workflowTemplateIds.length > 0
    ) {
      employeeData.workflowTemplateIds.forEach((id: string) => {
        formData.append("WorkflowTemplateIds", id)
      })
    }

    onSubmit(formData)
  }

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="w-full max-w-3xl p-0 max-h-[90vh] overflow-y-auto bg-card">
          <div className="p-6 border-b border-border">
            <DialogHeader className="gap-1">
              <DialogTitle className="text-2xl font-bold">
                Add Employee
              </DialogTitle>
              <DialogDescription className="text-sm text-muted-foreground">
                Fill in employee details and upload documents for the employee
                to sign.
              </DialogDescription>
            </DialogHeader>
          </div>

          <div className="p-6">
            {/* Documents To Be Signed Upload */}
            <div className="mb-6">
              <h3 className="text-base font-semibold text-foreground mb-3">
                Documents To Be Signed
              </h3>
              <p className="text-sm text-muted-foreground mb-3">
                Upload documents that the employee needs to sign (e.g.,
                Contract, NDA, Policies). You can upload multiple files.
              </p>
              <div
                className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-all ${isDragging
                    ? "border-primary bg-primary/5"
                    : fileError
                      ? "border-destructive"
                      : "border-border hover:border-border"
                  }`}
                onClick={() => !isSubmitting && fileInputRef.current?.click()}
                onDragOver={(e) => handleDragOver(e, setIsDragging)}
                onDragLeave={(e) => handleDragLeave(e, setIsDragging)}
                onDrop={handleDrop}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf,.doc,.docx"
                  title="Upload documents to be signed"
                  className="hidden"
                  disabled={isSubmitting}
                  multiple
                  onChange={handleFileInputChange}
                />
                <div className="space-y-2">
                  <FaFileUpload className="mx-auto text-4xl text-muted-foreground mb-2" />
                  <p className="text-sm text-muted-foreground font-medium">
                    Click to select files or drag and drop
                  </p>
                  <p className="text-xs text-muted-foreground">
                    PDF, DOC, or DOCX (multiple files allowed)
                  </p>
                </div>
              </div>
              {fileError && (
                <p className="mt-2 text-xs text-destructive">{fileError}</p>
              )}

              {/* Uploaded Documents List */}
              {documentsToBeSigned.length > 0 && (
                <div className="mt-4 space-y-2">
                  <p className="text-sm font-medium text-foreground">
                    {documentsToBeSigned.length} document
                    {documentsToBeSigned.length !== 1 ? "s" : ""} uploaded
                  </p>
                  {documentsToBeSigned.map((doc, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between bg-muted p-3 rounded-md border border-border"
                    >
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <FaFileUpload className="text-primary shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-foreground truncate">
                            {doc.name}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {formatFileSize(doc.file.size)}
                          </p>
                        </div>
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemoveDocument(index)}
                        disabled={isSubmitting}
                        className="text-destructive/60 hover:text-destructive shrink-0"
                      >
                        <FaTimes />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Employee Details */}
            <div>
              <h3 className="text-base font-semibold text-foreground">
                Employee Details
              </h3>
              <p className="text-sm text-muted-foreground mt-1">
                Fill in the employee details below.
              </p>
              <div className="mt-4">
                <EmployeeForm
                  formId={formId}
                  showActions={false}
                  onSubmit={handleSubmit}
                  availableJobs={availableJobs}
                />
              </div>
            </div>
          </div>

          <div className="sticky bottom-0 bg-card border-t border-border p-4">
            <div className="flex items-center justify-end gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button type="submit" form={formId} disabled={isSubmitting}>
                {submitLabel}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}
